import type { Me, SetupProgressDto, Tenant } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { TenantContext, useTenant, type TenantContextValue } from '../../lib/useTenant';
import { Button, SectionLoader } from '../../ui';
import { ArchivePage } from './Archive';
import { CollectionPage } from './Collection';
import { etaText, Bar, useWork } from './WorkProgress';
import { ConnectPage } from './Connect';
import { ControlsPage, KpisPage } from './Controls';
import { DataModelPage, EntityTypePage, NewEntityPage } from './DataModel';
import { EntityCardPage } from './EntityCard';
import { BuilderPage } from './Builder';
import { People } from './People';
import { Proposals } from './Proposals';
import { ProceduresPage } from './Procedures';
import { WizardPage } from './wizard/Wizard';
import { SitePage, SitesPage } from './Sites';
import { AccountsPage, CompanyPage, GovernancePage } from './Company';
import { NewProcess, Processes } from './Processes';
import { ProcessDetailPage } from './ProcessDetail';
import { RunDetailPage, Runs } from './Runs';
import { Sidebar } from './Sidebar';
import { SettingsPage } from '../SettingsPage';
import { TopBar } from '../TopBar';
import { rememberTenant } from '../../lib/last-tenant';
import { useBrand } from '../../lib/brand';
import { SoftwarePage } from './Software';
import { SoftwareDetailPage } from './SoftwareDetail';
import { StoredDataPage } from './StoredData';
import { Today } from './Today';

/** Area di lavoro di un'azienda: sezioni nell'URL (#/t/<tenant>/<sezione>/<id>). */
export function TenantShell({
  tenant,
  me,
  route,
  onLogout,
}: {
  tenant: Tenant;
  me: Me;
  route: string[];
  onLogout: () => void;
}) {
  const [section = 'oggi', id] = route;
  const isManager = tenant.role === 'owner' || tenant.role === 'admin';
  const scope = useQuery({
    queryKey: ['tenant', tenant.id, 'procedure-scope'],
    queryFn: () => api<{ tutte: boolean; aree: string[] }>(`/tenants/${tenant.id}/procedure-scope`),
    enabled: !isManager,
  });
  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      me,
      isManager,
      procedureAreas: isManager || scope.data?.tutte ? 'tutte' : (scope.data?.aree ?? []),
      t: (path: string) => `/tenants/${tenant.id}${path}`,
    }),
    [tenant, me, isManager, scope.data],
  );
  const go = (s: string) => navigate(`/t/${tenant.id}/${s}`);
  // Chi rientra dall'ingresso torna in quest'azienda (ADR 0118).
  useEffect(() => rememberTenant(tenant.id), [tenant.id]);
  // Configurazione guidata (stessa cache della pagina): finché non è finita il menu non c'è (non si può lasciare); dopo, la sua
  // voce sparisce.
  const progress = useQuery({
    queryKey: ['tenant', tenant.id, 'onboarding-progress'],
    queryFn: () => api<SetupProgressDto>(`/tenants/${tenant.id}/onboarding/progress`),
    enabled: isManager,
  });
  const g = progress.data?.guidata;
  // Un server che non conosce ancora la configurazione guidata (`guidata` assente) mostra il menu di sempre. Chi è
  // uscito dal contratto con i Comandi (ADR 0172) ha il menu: il percorso resta dov'è e lo riprende da «Torna al contratto».
  const guided = isManager && (progress.isPending || (!!g && !g.finita && !g.uscita));
  // Il percorso del contratto non è finito: fuori dalla configurazione si può sempre tornarci.
  const unfinished = isManager && !progress.isPending && !!g && !g.finita;
  // Ingresso nell'azienda (#/t/<tenant>): un responsabile riparte dalla configurazione guidata non finita, al passo
  // raggiunto, altrimenti dalla giornata; gli altri entrano dalla giornata.
  const entry = !route.length && isManager;
  useEffect(() => {
    if (entry && !progress.isPending)
      window.location.replace(`#/t/${tenant.id}/${guided ? 'configurazione' : 'oggi'}`);
  }, [entry, progress.isPending, guided, tenant.id]);

  let page;
  if (entry) page = <SectionLoader />;
  else if (section === 'esecuzioni') page = id ? <RunDetailPage runId={id} /> : <Runs />;
  else if (section === 'processi') {
    if (id === 'nuovo') page = <NewProcess />;
    else if (id === 'costruisci')
      page = <BuilderPage key={route[2] ?? 'nuova'} conversationId={route[2]} />;
    else if (id === 'rivedi')
      page = <BuilderPage key={`rivedi-${route[2]}`} processId={route[2]} />;
    else page = id ? <ProcessDetailPage processId={id} /> : <Processes />;
  } else if (section === 'persone')
    page = <People key={id ?? 'elenco'} importing={id === 'importa'} />;
  else if (section === 'archivio') page = <ArchivePage path={route.slice(1)} />;
  else if (section === 'sedi') page = id ? <SitePage key={id} id={id} /> : <SitesPage />;
  else if (section === 'impresa') page = <CompanyPage />;
  else if (section === 'organi') page = <GovernancePage />;
  else if (section === 'conti') page = <AccountsPage />;
  else if (section === 'e' && id)
    page = route[2] ? (
      <EntityCardPage key={`${id}/${route[2]}`} typeKey={id} rif={decodeURIComponent(route[2])} />
    ) : (
      <EntityTypePage key={id} typeKey={id} />
    );
  else if (section === 'nuova' && id) page = <NewEntityPage key={id} typeKey={id} />;
  else if (section === 'dati')
    page =
      id === 'salvati' ? (
        <StoredDataPage typeKey={route[2]} />
      ) : id === 'scheda' && route[2] && route[3] ? (
        <EntityCardPage key={`${route[2]}/${route[3]}`} typeKey={route[2]} rif={decodeURIComponent(route[3])} />
      ) : (
        <DataModelPage />
      );
  else if (section === 'configurazione') page = <WizardPage key={id ?? 'consigliato'} step={id} />;
  else if (section === 'procedure') page = <ProceduresPage area={id} />;
  else if (section === 'raccolta') page = <CollectionPage key={id ?? 'consigliato'} step={id} />;
  else if (section === 'software')
    page =
      id === 'scheda' && route[2] ? (
        <SoftwareDetailPage key={route[2]} id={route[2]} />
      ) : (
      <SoftwarePage
        key={route.join('/')}
        mail={id === 'posta'}
        outcome={
          id === 'esito' && route[2]
            ? { ok: route[2] === 'ok', message: decodeURIComponent(route[3] ?? '') }
            : undefined
        }
      />
      );
  else if (section === 'collega') page = <ConnectPage kind={id} target={route[2]} />;
  else if (section === 'impostazioni') page = <SettingsPage me={me} section={id} base={`/t/${tenant.id}/impostazioni`} />;
  else if (section === 'controlli') page = <ControlsPage />;
  else if (section === 'indicatori') page = <KpisPage />;
  else if (section === 'proposte') page = <Proposals />;
  else page = <Today entry={!route.length} />;

  // Voce evidenziata nel menu: il tipo anche dalla sua scheda (ADR 0112).
  const active =
    section === 'e' || section === 'nuova' ? `e/${id}` : section === 'dati' && id === 'scheda' ? `e/${route[2]}` : section === 'dati' && id === 'salvati' ? 'dati/salvati' : section;

  return (
    <TenantContext.Provider value={value}>
      {/* Sempre la stessa impaginazione: durante la configurazione guidata senza menu (non si può lasciare), mentre si
          capisce se è finita con il posto del menu vuoto. */}
      <ShellLayout
        active={active}
        menu={isManager && progress.isPending ? 'attesa' : guided ? 'no' : 'si'}
        onLogout={onLogout}
        back={unfinished && section !== 'configurazione' ? () => go('configurazione') : undefined}
      >
        {value.isManager && !guided && <WorkBand onOpen={() => go('raccolta')} />}
        {isManager && progress.isPending ? <SectionLoader /> : page}
      </ShellLayout>
    </TenantContext.Provider>
  );
}

/** Menu laterale fisso sullo schermo grande; sul telefono si apre con il pulsante e si chiude cambiando pagina. */
function ShellLayout({
  active,
  menu,
  onLogout,
  back,
  children,
}: {
  active: string;
  /** `attesa` = il posto del menu c'è ma è vuoto, finché non si sa se mostrarlo. */
  menu: 'si' | 'no' | 'attesa';
  onLogout: () => void;
  /** Ritorno alla configurazione guidata, quando la persona ne è uscita per un momento. */
  back?: () => void;
  children: ReactNode;
}) {
  const { tenant, me } = useTenant();
  // Colori e logo dell'azienda (ADR 0117).
  const { logo } = useBrand(tenant.id);
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [active]);
  return (
    <div className="lg:flex">
      {menu !== 'no' && (
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 bg-white shadow-[0_0_24px_rgba(89,102,122,0.08)] lg:block">
          {menu === 'si' && <Sidebar active={active} logo={logo} />}
        </aside>
      )}
      {open && menu === 'si' && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button className="absolute inset-0 bg-ink/30" aria-label="Chiudi il menu" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl">
            <Sidebar active={active} logo={logo} />
          </aside>
        </div>
      )}
      <main className="min-w-0 flex-1 px-3 pb-6 pt-3 lg:px-6 lg:pt-4">
        {/* Barra in alto (ADR 0118): l'azienda, che si cambia da qui, e l'utente con le impostazioni. */}
        <TopBar
          me={me}
          current={tenant}
          onLogout={onLogout}
          start={
            menu === 'si' ? (
              <button className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-accent hover:bg-accent/10 lg:hidden" aria-label="Apri il menu" onClick={() => setOpen(true)}>
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
            ) : undefined
          }
          extra={
            back && (
              <Button variant="ghost" className="hidden sm:block" onClick={back}>
                ← Torna al contratto
              </Button>
            )
          }
        />
        <div className="min-w-0">{children}</div>
      </main>
    </div>
  );
}

/**
 * Fascia della coda di lavorazione (ADR 0043): finché il sistema sta leggendo (posta, file, programmi) dice quanto
 * manca, con la barra; sparisce da sola quando ha finito.
 */
function WorkBand({ onOpen }: { onOpen: () => void }) {
  const work = useWork();
  const a = work.data?.active;
  if (!a || !a.sources) return null;
  return (
    <button type="button" onClick={onOpen} className="mb-5 block w-full rounded-lg border border-black/10 bg-white px-4 py-2 text-left text-sm shadow-sm hover:bg-black/[0.02]">
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Sto leggendo i vostri dati</span>
        <span className="text-ink/60">
          {a.sources === 1 ? '1 fonte' : `${a.sources} fonti`} · {a.done.toLocaleString('it-IT')} di {a.items.toLocaleString('it-IT')} elementi
          {work.data?.sources.some((s) => s.paused) ? ' · in pausa: apri per vedere perché' : a.eta_seconds !== null ? ` · ${etaText(a.eta_seconds)} alla fine` : ' · calcolo il tempo che manca…'}
        </span>
        <span className="flex-1" />
        <span className="text-xs text-ink/50">{a.percent}%</span>
      </span>
      <span className="mt-1.5 block">
        <Bar percent={a.percent} />
      </span>
    </button>
  );
}
